import React, { useState, useEffect, useRef } from "react";
import styles from "./VectorSearch.module.css"; // Create this CSS module for styling

const BASE_URL = "https://vector-search-api-398932329807.us-central1.run.app";

const DEFAULT_TOP_K = 10;
const DEFAULT_SIMILARITY = 0.0;
const PLACEHOLDER_EXAMPLES = ["climbing", "hiking", "kayaking"];

// Hybrid search scoring configuration
const SCORING_CONFIG = {
  vectorWeight: 0.6,     // 60% weight for vector similarity
  textWeight: 0.4,       // 40% weight for text matching
  textScores: {
    businessName: 0.8,   // High score for name matches
    category: 0.6,       // Medium score for category matches
    description: 0.3     // Low score for description matches
  }
};

function VectorSearch() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [topK, setTopK] = useState(DEFAULT_TOP_K);
  const [similarity, setSimilarity] = useState(DEFAULT_SIMILARITY);
  const [searchTime, setSearchTime] = useState(null);
  const [totalResults, setTotalResults] = useState(null);
  const [hybridEnabled, setHybridEnabled] = useState(true);
  const debounceRef = useRef();

  // Text matching scoring function
  const calculateTextScore = (result, searchQuery) => {
    if (!searchQuery.trim()) return 0;
    
    const query = searchQuery.toLowerCase();
    const metadata = result.metadata || {};
    
    let textScore = 0;
    
    // Check business name
    const businessName = (metadata.vendor_name || '').toLowerCase();
    if (businessName.includes(query)) {
      textScore = Math.max(textScore, SCORING_CONFIG.textScores.businessName);
    }
    
    // Check category
    const category = (metadata.category || '').toLowerCase();
    if (category.includes(query)) {
      textScore = Math.max(textScore, SCORING_CONFIG.textScores.category);
    }
    
    // Check description/content
    const description = (metadata.content || '').toLowerCase();
    if (description.includes(query)) {
      textScore = Math.max(textScore, SCORING_CONFIG.textScores.description);
    }
    
    return textScore;
  };

  // Hybrid scoring function
  const calculateHybridScore = (result, searchQuery) => {
    const vectorScore = result.similarity_score || 0;
    const textScore = calculateTextScore(result, searchQuery);
    
    return (vectorScore * SCORING_CONFIG.vectorWeight) + (textScore * SCORING_CONFIG.textWeight);
  };

  // Health check on mount
  useEffect(() => {
    fetch(`${BASE_URL}/health`)
      .then((res) => res.ok ? null : Promise.reject())
      .catch(() => setError("API health check failed."));
  }, []);

  // Clear results when query is empty
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setSearchTime(null);
      setTotalResults(null);
      setError("");
    }
  }, [query]);

  const doSearch = async () => {
    try {
      setLoading(true);
      setError("");
      const url = `${BASE_URL}/search-simple?q=${encodeURIComponent(query)}&top_k=${topK}&similarity_threshold=${similarity}`;

      const res = await fetch(url);
      
      if (!res.ok) {
        const errorText = await res.text();
        console.error("API Error Response:", errorText);
        throw new Error(`Search failed (${res.status}): ${errorText}`);
      }
      
      const responseText = await res.text();

      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error("JSON Parse Error:", parseError);
        throw new Error(`Invalid JSON response: ${responseText.substring(0, 100)}...`);
      }
      
      let processedResults = data.results || [];
      
      // Apply hybrid scoring if enabled
      if (hybridEnabled && processedResults.length > 0) {
        processedResults = processedResults.map(result => ({
          ...result,
          hybrid_score: calculateHybridScore(result, query),
          text_score: calculateTextScore(result, query)
        }));
        
        // Re-sort by hybrid score
        processedResults.sort((a, b) => b.hybrid_score - a.hybrid_score);
      }
      
      setResults(processedResults);
      setSearchTime(data.search_time_ms);
      setTotalResults(data.total_results);
    } catch (err) {
      console.error("Search Error:", err);
      setError(err.message || "Unknown error");
      setResults([]);
      setSearchTime(null);
      setTotalResults(null);
    } finally {
      setLoading(false);
    }
  };

  // Error boundary fallback
  if (error) {
    return (
      <div className={styles.error}>
        <p>Error: {error}</p>
        <button onClick={() => setError("")}>Dismiss</button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Vector Search</h2>
      <div className={styles.searchBox}>
        <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyPress={e => e.key === 'Enter' && query.trim() && doSearch()}
          placeholder={`Try searching for: ${PLACEHOLDER_EXAMPLES.join(", ")}`}
          className={styles.input}
        />
        <button
          onClick={() => query.trim() && doSearch()}
          disabled={loading || !query.trim()}
          className={styles.button}
        >
          {loading ? "Searching..." : "Search"}
        </button>
      </div>
      <div className={styles.filters}>
        <label>
          Top K:
          <input
            type="number"
            min={1}
            max={50}
            value={topK}
            onChange={e => setTopK(Number(e.target.value))}
            className={styles.filterInput}
          />
        </label>
        <label>
          Similarity Threshold:
          <input
            type="number"
            min={0}
            max={1}
            step={0.01}
            value={similarity}
            onChange={e => setSimilarity(Number(e.target.value))}
            className={styles.filterInput}
          />
        </label>
        <label>
          <input
            type="checkbox"
            checked={hybridEnabled}
            onChange={e => setHybridEnabled(e.target.checked)}
          />
          Enable Hybrid Search
        </label>
      </div>
      {loading && <div className={styles.loading}>Loading...</div>}
      {searchTime !== null && (
        <div className={styles.stats}>
          <span>Search time: {searchTime} ms</span>
          <span>Total results: {totalResults}</span>
        </div>
      )}
      <div className={styles.results}>
        {results.map((r) => (
          <div key={r.id} className={styles.card}>
            <div className={styles.cardHeader}>
              <span className={styles.vendorName}>{r.metadata.vendor_name}</span>
              <span className={styles.category}>{r.metadata.category}</span>
            </div>
            <div className={styles.cardBody}>
              <div><b>State:</b> {r.metadata.state}</div>
              <div><b>Rating:</b> {r.metadata.rating}</div>
              <div><b>Address:</b> {r.metadata.formatted_address}</div>
              <div><b>Website:</b> <a href={r.metadata.website} target="_blank" rel="noopener noreferrer">{r.metadata.website}</a></div>
            </div>
            <div className={styles.cardFooter}>
              <span>Vector: {r.similarity_score?.toFixed(3) || 'N/A'}</span>
              {hybridEnabled && (
                <>
                  <span>Text: {r.text_score?.toFixed(3) || '0.000'}</span>
                  <span>Hybrid: {r.hybrid_score?.toFixed(3) || 'N/A'}</span>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default VectorSearch;
